import { apiFetch } from '@/lib/api';
import type { Topic, TopicDetails } from '@/types/topic';

interface ListParams {
  type_id?: number;
  status?: 'draft' | 'active' | 'archived';
  investor?: string;
}

// The admin list paginates client-side (with a 20/50/100 size selector), so we
// need every row, not just the backend's default page. Walk the paginated
// envelope until all pages are collected. `pageSize=200` is the backend's
// MAX_PAGE_SIZE, so this is at most ceil(total/200) round-trips.
export async function listTopics(params: ListParams = {}): Promise<Topic[]> {
  const base = new URLSearchParams();
  if (params.type_id !== undefined) base.set('type_id', String(params.type_id));
  if (params.status) base.set('status', params.status);

  const PAGE = 200;
  const all: Topic[] = [];
  for (let page = 1; ; page++) {
    const qs = new URLSearchParams(base);
    qs.set('page', String(page));
    qs.set('pageSize', String(PAGE));
    const r = await apiFetch<
      { data?: Topic[]; topics?: Topic[]; totalPages?: number } | Topic[]
    >(`/topic?${qs}`);
    // Legacy shapes: a raw array has no pagination, so it's the whole result.
    if (Array.isArray(r)) return r;
    const batch = r.data ?? r.topics ?? [];
    all.push(...batch);
    const totalPages = r.totalPages ?? 1;
    if (page >= totalPages || batch.length === 0) break;
  }
  return all;
}

// Read through the ADMIN route (requireAdmin), never the public GET /topic/:id.
// The public route display-filters details for non-admin callers; an expired
// token would silently yield a language-stripped payload that a later save
// would persist, erasing hidden-language translations. Here an expired token
// 401s → re-login, so the editor never loads a truncated topic.
export async function getTopic(id: string): Promise<Topic> {
  return apiFetch<Topic>(`/topic-admin/${id}`);
}

export interface CreateTopicInput {
  type_id: number;
  status?: 'draft' | 'active';
  details: TopicDetails;
  display_sections?: Array<{ id: number; tag_name: string; sort_index: number }>;
  attachment_ids?: string[];
}

export async function createTopic(input: CreateTopicInput): Promise<Topic> {
  return apiFetch<Topic>('/topic-admin', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateTopic(id: string, input: Partial<CreateTopicInput>): Promise<Topic> {
  return apiFetch<Topic>(`/topic-admin/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteTopic(id: string): Promise<void> {
  await apiFetch(`/topic-admin/${id}`, { method: 'DELETE' });
}
